import { useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { LoginContext } from '../contexts/LoginContext';
import { USER_STORAGE_KEY, LOGOUT_URL } from '../utils/constant';
import { logger } from '../utils/logger';
import useAxios from './useAxios';

/**
 * Hook personnalisé pour gérer la déconnexion de l'utilisateur
 * Centralise la logique de logout pour tous les composants
 *
 * Effectue les actions suivantes :
 * 1. Appelle l'API POST /auth/logout avec le refresh token
 * 2. Nettoie le localStorage
 * 3. Réinitialise le contexte utilisateur
 * 4. Redirige vers la page de connexion
 *
 * @returns {Function} logout - Fonction async pour déconnecter l'utilisateur
 *
 * @example
 * const logout = useLogout();
 *
 * <button onClick={logout}>Déconnexion</button>
 */
export const useLogout = () => {
  const { setCurrentUser } = useContext(LoginContext);
  const navigate = useNavigate();
  const { postWithAxios } = useAxios();

  const logout = useCallback(async () => {
    try {
      // Récupérer le refresh token depuis localStorage
      const currentUserData = localStorage.getItem(USER_STORAGE_KEY);
      const user = currentUserData ? JSON.parse(currentUserData) : null;
      const refreshToken = user?.tokens?.refreshToken;

      // Appeler l'API logout si un refresh token existe
      if (refreshToken) {
        logger.debug('Calling logout API with refresh token');
        await postWithAxios(LOGOUT_URL, { refreshToken });
        logger.debug('Logout API call successful');
      } else {
        logger.warn('No refresh token found, skipping API call');
      }
    } catch (err) {
      // Ne pas bloquer la déconnexion en cas d'erreur API
      logger.error('Error calling logout API:', err);
    } finally {
      // Toujours nettoyer les données locales et rediriger
      // même si l'appel API échoue
      logger.debug('Cleaning up user session');
      localStorage.removeItem(USER_STORAGE_KEY);
      setCurrentUser(undefined);
      navigate('/');
    }
  }, [setCurrentUser, navigate, postWithAxios]);

  return logout;
};